import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { Aiera } from 'aiera-sdk';
import { withGetUser, withUserPreferences } from 'graphql/user';
import { generateTabURL, get, getPreference, parseTabId } from 'utils';
import { config } from 'configuration';
import { withUrlContext } from 'hoc/url';
import { PREFERENCES, TAB_TYPES } from 'consts';
import { AieraChatUI } from './ui';

export class AieraChat extends PureComponent {
    static displayName = 'AieraChatContainer';

    static propTypes = {
        tabs: PropTypes.arrayOf(PropTypes.string),
        history: PropTypes.objectOf(PropTypes.any).isRequired,
        location: PropTypes.objectOf(PropTypes.any).isRequired,
        preferences: PropTypes.objectOf(PropTypes.any),
        user: PropTypes.objectOf(PropTypes.any),
        styles: PropTypes.objectOf(PropTypes.any)
    };

    static defaultProps = {
        tabs: [],
        preferences: {},
        user: undefined,
        styles: undefined
    };

    constructor(props) {
        super(props);
        this.loadComponent = this.loadComponent.bind(this);
        this.setup = this.setup.bind(this);

        this.state = {
            loaded: false
        };
    }

    componentDidMount() {
        this.setup();
    }

    componentDidUpdate() {
        this.setup();
    }

    componentWillUnmount() {
        clearInterval(this.pollIframe);
    }

    setup() {
        const { user } = this.props;
        const { loaded } = this.state;
        if (!loaded && get(user, 'id')) {
            this.setState({ loaded: true }, () => {
                this.pollIframe = setInterval(() => {
                    const ifr = document.getElementById('aiera-chat-frame');
                    if (ifr) {
                        this.loadComponent();
                        clearInterval(this.pollIframe);
                    }
                }, 100);
            });
        }
    }

    loadComponent() {
        const { user, history, location } = this.props;
        const apiKey = get(user, 'apiKeyPublic', config.PUBLIC_API_KEY);
        const aieraChatDisableWebSearch = get(user, 'permissions', []).includes('aierachat_disable_web_search');
        this.aieraChat = new Aiera.Module(
            `${config.PUBLIC_API_ENDPOINT}modules/AieraChat/index.html`,
            'aiera-chat-frame'
        );
        this.aieraChat.load().then(() => {
            this.aieraChat.authenticateApiKey(apiKey);
        });
        this.aieraChat.on('authenticated', () => {
            this.aieraChat.configure({
                hideSettings: true,
                options: {
                    aieraChatEnableAbly: true,
                    aieraChatEnablePolling: false,
                    aieraChatCollectInternalFeedback: false,
                    aieraChatDisableSourceNav: true,
                    aieraChatDisableWebSearch,
                    showSearch: true,
                    showSentiment: false,
                    darkMode: false
                }
                // tracking: {
                //     userId: get(user, 'id', 'login-with-public-api-key-1') // Use the current userId value
                // }
            });
        });
        this.aieraChat.on('chat-source', chatSource => {
            const sourceType = get(chatSource, 'targetType');
            const sourceId = get(chatSource, 'targetId');
            if (['event', 'transcript'].includes(sourceType)) {
                const tabPath = generateTabURL({ location, eventId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'filing') {
                const tabPath = generateTabURL({ location, filingId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'attachment') {
                const tabPath = generateTabURL({ location, documentId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'external') {
                const sourceUrl = get(chatSource, 'url');
                if (sourceUrl) {
                    const url = new URL(sourceUrl, window.origin);
                    window.open(url, '_blank');
                }
            }
        });
        this.aieraChat.on('chat-citation', chatCitation => {
            const sourceType = get(chatCitation, 'sourceType');
            const sourceId = get(chatCitation, 'sourceId');
            const sourceParentType = get(chatCitation, 'sourceParentType');
            const sourceParentId = get(chatCitation, 'sourceParentId');
            if (sourceType === 'transcript' && sourceParentType === 'event') {
                const tabPath = generateTabURL({ location, eventId: sourceParentId, page: 'text', pageId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'filing') {
                const tabPath = generateTabURL({ location, filingId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'attachment') {
                const tabPath = generateTabURL({ location, documentId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'event') {
                const tabPath = generateTabURL({ location, eventId: sourceId });
                history.push(tabPath);
            } else if (sourceType === 'external') {
                const url = new URL(chatCitation.url, window.origin);
                window.open(url, '_blank');
            }
        });
    }

    render() {
        const { styles, tabs, preferences } = this.props;
        const tabType = tabs.length > 0 ? parseTabId(tabs[0]).tabType : undefined;
        const isFocused = getPreference(preferences, PREFERENCES.eventFocusMode, false);
        const isFullScreen = getPreference(preferences, PREFERENCES.floatingTabFullScreen, false);

        let tabPadding = 0;

        if (!isFullScreen) {
            if (tabType === TAB_TYPES.news) {
                tabPadding = 960;
            }

            if (tabType === TAB_TYPES.report) {
                tabPadding = 760;
            }

            if (tabType === TAB_TYPES.filing) {
                tabPadding = 1060;
            }

            if (tabType === TAB_TYPES.pdfSlides) {
                tabPadding = 1200;
            }

            if (tabType === TAB_TYPES.pdfPresentation) {
                tabPadding = 1200;
            }

            if (tabType === TAB_TYPES.document) {
                tabPadding = 1200;
            }

            if (tabType === TAB_TYPES.research) {
                tabPadding = 1200;
            }

            if (tabType === TAB_TYPES.streetAccount) {
                tabPadding = 740;
            }

            if (tabType === TAB_TYPES.dataRecord) {
                tabPadding = 740;
            }

            if (tabType === TAB_TYPES.event || tabType === TAB_TYPES.audioCall) {
                tabPadding = isFocused ? 650 : 1000;
            }
        }

        return <AieraChatUI styles={styles} tabPadding={tabPadding} />;
    }
}

export const AieraChatContainer = compose(
    withUrlContext(['history', 'location', 'tabs']),
    withGetUser({
        fetchPolicy: 'cache-first',
        variables: { withDetails: true, withDev: true, withOrganization: true }
    }),
    withUserPreferences({ fetchPolicy: 'cache-first' })
)(AieraChat);
